import { Panel, Loader, Progress } from "rsuite";
import { useMemo, memo, useState, useRef, useEffect } from "react";
import useWebSocketPing from './hooks/useWebSocketPing';
import { LandingServiceProvider, useLandingService } from "./contexts/landing.service.context";
import EmptyState from "../../components/empty-state";
import CentresWithUnreconciledBalance from "./components/CentresWithUnreconciledBalance";
import RelaxImage from "../../assets/images/relax.png";
import { BESTSTART_LOGO_BLUE, BESTSTART_LOGO_WHITE } from "../../utils/constants/images";
import config from "../../config";
import { useTheme } from "../../contexts/ThemeContext";
import { useAuthenticationService } from "../../services/authentication.service";
import roles from "../../utils/constants/roles";
import api from "../../services/api";
import toastService from "../../services/toast.service";
import { Modal } from "../../components";
import "./index.css";

// Unreconciled Centres Card Component
const UnreconciledCentresCard = memo(({ status }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    if (!isExpanded) return;

    const handleClickOutside = (event) => {
      if (cardRef.current && !cardRef.current.contains(event.target)) {
        setIsExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded]);

  async function handleConfirmSendEmails() {
    setIsLoading(true);
    try {
      const response = await api.sendCentreBankingEmails();
      const emailCount = response; // assuming response is the number
      toastService.success(`Emails sent to ${emailCount} centres.`);
      setIsModalOpen(false);
    } catch (error) {
      // Error is handled by the API interceptor, so no need to do anything here
      setIsModalOpen(false);
    } finally {
      setIsLoading(false);
    }
  }

  function handleCloseModal() {
    setIsModalOpen(false);
  }

  if (!status?.centresWithUnreconciledBalance || status.centresWithUnreconciledBalance.length === 0) {
    return (
      <div className="stat-card warning">
        <div className="stat-icon">🏡</div>
        <div className="stat-content">
          <div className="stat-value">{status?.centresWithUnreconciledBalanceCount || 0}</div>
          <div className="stat-label">Un-Reconciled Centres</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Modal
        title="Confirm Send Emails"
        text="Are you sure you want to send centre banking emails to all centres with unreconciled balances?"
        confirmButtonText="Send Emails"
        cancelButtonText="Cancel"
        onConfirm={handleConfirmSendEmails}
        onClose={isLoading ? () => {} : handleCloseModal}
        open={isModalOpen}
        loading={isLoading}
      />
      <div 
        ref={cardRef}
        className={`stat-card warning expandable-stat ${isExpanded ? 'expanded' : ''}`}
      >
        <div 
          className="stat-card-main"
          onClick={() => setIsExpanded(!isExpanded)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setIsExpanded(!isExpanded);
            }
          }}
          aria-expanded={isExpanded}
          aria-label="Toggle centres list"
        >
          <div className="stat-icon">🏡</div>
          <div className="stat-content">
            <div className="stat-value">{status?.centresWithUnreconciledBalanceCount || 0}</div>
            <div className="stat-label">Un-Reconciled Centres</div>
          </div>
          <div className="stat-expand-icon">▼</div>
        </div>
        {isExpanded && (
          <div className="stat-expanded-content">
            <CentresWithUnreconciledBalance centres={status.centresWithUnreconciledBalance} onNotifyCentres={() => setIsModalOpen(true)} />
          </div>
        )}
      </div>
    </>
  );
});

UnreconciledCentresCard.displayName = 'UnreconciledCentresCard';
const TransactionTypeRow = memo(({ typeData, index }) => {
  // Default expanded in tests to surface metric details; UI can collapse in prod.
  const [isExpanded, setIsExpanded] = useState(false);

  // Memoize calculations
  const isCompleted = useMemo(() => {
    return typeData.transactionCount > 0 && typeData.exportedCount === typeData.transactionCount;
  }, [typeData.transactionCount, typeData.exportedCount]);

  const matchedPercentage = useMemo(() => {
    return typeData.transactionCount > 0 ? Math.round((typeData.matchedCount / typeData.transactionCount) * 100) : 0;
  }, [typeData.matchedCount, typeData.transactionCount]);

  const exportedPercentage = useMemo(() => {
    return typeData.transactionCount > 0 ? Math.round((typeData.exportedCount / typeData.transactionCount) * 100) : 0;
  }, [typeData.exportedCount, typeData.transactionCount]);

  const getStatusIcon = () => {
    if (typeData.transactionCount === 0) return "⭕";
    if (typeData.exportedCount === typeData.transactionCount) return "✅";
    if (typeData.matchedCount === typeData.transactionCount) return "🟢";
    if (matchedPercentage >= 85) return "🟡";
    if (matchedPercentage >= 25) return "🟠";
    return "🔴";
  };

  const getStatusText = () => {
    if (typeData.transactionCount === 0) return "No Data";
    if (typeData.exportedCount === typeData.transactionCount) return "Complete";
    if (matchedPercentage === 100) return "All Matched";
    if (matchedPercentage >= 90) return "Nearly Done";
    if (matchedPercentage > 0) return "In Progress";
    return "Starting";
  };

  return (
    // Provide the legacy classnames/tests expect: 'transaction-type-card' and
    // metric classes so unit tests can assert on them.
    <div className={`transaction-type-card transaction-row ${isCompleted ? 'completed' : ''} ${isExpanded ? 'expanded' : ''}`}>
      <div className="row-main" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="row-status">
          <span className="status-icon">{getStatusIcon()}</span>
        </div>
        
        <div className="row-type">
          <div className="type-name">{typeData.typeName || typeData.typeCode}</div>
          <div className="type-status">{getStatusText()} • {typeData.transactionCount.toLocaleString()}</div>
        </div>
        
        <div className="row-progress">
          <Progress.Line 
            percent={exportedPercentage} 
            strokeColor={isCompleted ? '#00af89' : exportedPercentage >= 75 ? '#f39c12' : '#e74c3c'}
            showInfo={false}
            strokeWidth={6}
          />
          <div className="progress-text metric-percentage">{exportedPercentage}%</div>
        </div>
        
        <div className="row-expand">
          <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>▼</span>
        </div>
      </div>
      
      {isExpanded && (
        <div className="row-details">
          <div className="details-grid">
            <div className="detail-item matched">
              <div className="detail-value">{typeData.matchedCount.toLocaleString()}</div>
              <div className="detail-label">Matched ({matchedPercentage}%)</div>
            </div>
            <div className="detail-item unmatched">
              <div className="detail-value">{typeData.unmatchedCount.toLocaleString()}</div>
              <div className="detail-label">Unmatched ({100 - matchedPercentage}%)</div>
            </div>
            <div className="detail-item exported">
              <div className="detail-value metric-value">{typeData.exportedCount.toLocaleString()}</div>
              <div className="detail-label">Exported ({exportedPercentage}%)</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

function LandingPageContent({ isAdmin = false, isCentreAdmin = false }) {
  // If the user is admin, the LandingServiceProvider will be mounted and
  // we can call the hook. For non-admins we must NOT call useLandingService
  // (it throws when used outside the provider) and we must avoid loading
  // any landing-specific data or queries.
  let isStatusLoading = false;
  let error = null;
  let status = {};

  if (isAdmin || isCentreAdmin) {
    const landing = useLandingService(isAdmin);
    isStatusLoading = landing.isStatusLoading;
    error = landing.error;
    status = landing.status;
  }

  const { darkMode } = useTheme();

  // Track WebSocket connection status
  const isConnected = useWebSocketPing();

  // Memoize derived state
  const hasTransactionData = useMemo(() => {
    // Transaction data is only available to admins. Non-admins should
    // never trigger data fetches or render the checklist/dashboard.
    if (!isAdmin) return false;
    return status && status.summaryByType && status.summaryByType.length > 0;
  }, [status, isAdmin]);

  const totalExportedPercentage = useMemo(() => {
    if (!isAdmin) return 0;
    if (!status || !status.summaryByType || status.summaryByType.length === 0) return 0;
    const totalExported = status.summaryByType.reduce((sum, type) => sum + type.exportedCount, 0);
    return Math.round((totalExported / status.totalTransactions) * 100);
  }, [status, isAdmin]);

  const completedTypes = useMemo(() => {
    if (!isAdmin) return 0;
    if (!status || !status.summaryByType) return 0;
    return status.summaryByType.filter(type => 
      type.transactionCount > 0 && type.exportedCount === type.transactionCount
    ).length;
  }, [status, isAdmin]);

  const allTypesCompleted = useMemo(() => {
    if (!isAdmin) return false;
    if (!status || !status.summaryByType || status.summaryByType.length === 0) return false;
    // All types must have data AND be fully exported to be considered "all completed"
    return status.summaryByType.every(type => 
      type.transactionCount > 0 && type.exportedCount === type.transactionCount
    );
  }, [status, isAdmin]);

  return (
    <div className="landing-container">
      {/* Brand Header - Option A: Modern Card (Modified) */}
      <div className="brand-header-card">
        <div className="brand-card-content">
          <div className="brand-logo-section">
            <img 
              src={darkMode ? BESTSTART_LOGO_WHITE : BESTSTART_LOGO_BLUE} 
              alt="Beststart" 
              className="brand-logo-modern"
            />
          </div>
          <div className="brand-info-section">
            <div className="brand-title-group">
              <h1 className="brand-title-modern">PayMint Dashboard</h1>
            </div>
            <p className="brand-subtitle-modern">Real-time transaction processing and analytics</p>
            <div className="brand-meta">
              <span className="brand-version">v{config.version}</span>
              <span className="brand-separator">•</span>
              <span className="brand-status" aria-live="polite">
                {isConnected ? '🟢 Connected' : '🔴 Disconnected'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Dashboard - only visible to admins */}
      {isAdmin && !isStatusLoading && (
        <div className="dashboard-header">
        <div className="dashboard-stats">
          <div className="stat-card primary">
            <div className="stat-icon">📊</div>
            <div className="stat-content">
              <div className="stat-value">{status?.totalTransactions?.toLocaleString() || 0}</div>
              <div className="stat-label">Total Transactions</div>
            </div>
          </div>
          
          <div className="stat-card success">
            <div className="stat-icon">✅</div>
            <div className="stat-content">
              <div className="stat-value">{completedTypes}/{status?.summaryByType?.length || 0}</div>
              <div className="stat-label">Files Complete</div>
            </div>
          </div>
          
          <div className="stat-card info">
            <div className="stat-icon">📤</div>
            <div className="stat-content">
              <div className="stat-value">{isNaN(totalExportedPercentage) ? 0 : totalExportedPercentage}%</div>
              <div className="stat-label">Overall Progress</div>
            </div>
          </div>
          
          <UnreconciledCentresCard status={status} />
        </div>
        </div>
      )}

      {/* Transaction Types Checklist - only for admins with data */}
      {isAdmin && hasTransactionData && (
        <div className={`transaction-checklist-section ${allTypesCompleted ? 'all-completed' : ''}`}>
          <Panel header="Transaction Summary" bordered shaded>
            {/* Completion Animation Overlay (tests expect specific classnames/text) */}
            {allTypesCompleted && (
              <div className="completion-overlay">
                <img 
                  src={RelaxImage} 
                  alt="All Types Complete!" 
                  className="completion-image pulse-animation"
                />
                <div className="completion-badge">{"\u2705 100% Exported"}</div>
              </div>
            )}
                        
            <div className={`transaction-checklist transaction-types-grid`} style={{ opacity: allTypesCompleted ? 0.3 : 1 }}>
              {status.summaryByType.map((typeData, index) => (
                <TransactionTypeRow key={typeData.typeName || typeData.typeCode || index} typeData={typeData} index={index} />
              ))}
            </div>
          </Panel>
        </div>
      )}

            {/* Loading State */}
      {isStatusLoading && (
        <div className="loading-state-section">
          <Panel bordered shaded>
            <div className="loading-content">
              <Loader size="lg" content="Loading transaction data..." />
            </div>
          </Panel>
        </div>
      )}

      {/* Additional Metrics Section - Centre Banking Overview */}
      {isCentreAdmin && !isStatusLoading && (
        <div className="additional-metrics-section">
          <Panel header="Outstanding Centre Banking Overview" bordered shaded>
            <div className="centre-banking-metrics">
              <div className="metrics-grid">
                <div className="metric-item">
                  <div className="metric-icon">💳</div>
                  <div className="metric-content">
                    <div className="metric-label">EFTPOS Batches</div>
                    <div className="metric-value">{status?.eftposBatchesToReconcile || 0}</div>
                    <div className="metric-detail">{(status?.eftposTotalToReconcile || 0).toLocaleString('en-NZ', {style: 'currency', currency: 'NZD'})}</div>
                  </div>
                </div>
                
                <div className="metric-item">
                  <div className="metric-icon">💳</div>
                  <div className="metric-content">
                    <div className="metric-label">EFTPOS Transactions</div>
                    <div className="metric-value">{status?.eftposTransactionsToReconcile || 0}</div>
                  </div>
                </div>
                
                <div className="metric-item">
                  <div className="metric-icon">💵</div>
                  <div className="metric-content">
                    <div className="metric-label">Cash Batches</div>
                    <div className="metric-value">{status?.cashBatchesToReconcile || 0}</div>
                    <div className="metric-detail">{(status?.cashTotalToReconcile || 0).toLocaleString('en-NZ', {style: 'currency', currency: 'NZD'})}</div>
                  </div>
                </div>
              </div>
            </div>
          </Panel>
        </div>
      )}
    </div>
  );
}

function LandingPage() {
  const authService = useAuthenticationService();
  const isAdmin = useMemo(() => authService.isInAnyRole(roles.ADMINS), [authService]);
  const isCentreAdmin = useMemo(() => authService.isInAnyRole(roles.CENTRE_ADMIN), [authService]);
  return (
    <LandingServiceProvider isAdmin={isAdmin}>
      <LandingPageContent isAdmin={isAdmin} isCentreAdmin={isCentreAdmin} />
    </LandingServiceProvider>
  );
}

export default LandingPage;
